import {
    INCENTIVE_DISPLAY_STATE,
    INCENTIVE_TYPE,
    SP_STATUS_KEY_PAYMENT_FAILED,
    STUDY_PERSON_INCENTIVE_STATUS
} from 'utils/constants';

/**
 * Derives the incentive display state from a study person record.
 *
 * The display state determines which Badge variant to render in the table
 * and participant popup. It is derived from multiple fields:
 *
 * - **failed** — `incentive_status` is `'failed'` or participant status is "Payment Failed" (`status === 8`)
 * - **pending** — `incentive_status` is `'pending'` (for new records, `incentive_sent_at` is set when entering pending state),
 *   or legacy first-party payments (pre-2026) where `incentive_status='paid'` but `incentive_sent_at` not yet set
 * - **processing** — `incentive_status` is `'processing'`
 * - **paid** — `incentive_status` is `'paid'` and the payment has been sent
 * - **null** — not yet paid; no badge should be rendered
 *
 * @param {Object} studyPerson - A study_person record from the API.
 * @param {string} [studyPerson.incentive_status] - 'paid', 'pending', 'processing', 'failed', or 'unpaid'.
 * @param {string|null} [studyPerson.incentive_sent_at] - ISO date set when status becomes 'pending' for first-party payments, or when actually paid for other types. May be null for legacy records.
 * @param {number} [studyPerson.incentive_type] - Numeric payment channel (see INCENTIVE_TYPE).
 * @param {number} [studyPerson.status] - Participant status key (e.g. SP_STATUS_KEY_PAYMENT_FAILED = 8).
 * @returns {import('utils/constants').INCENTIVE_DISPLAY_STATE[keyof import('utils/constants').INCENTIVE_DISPLAY_STATE]|null}
 */
const INCENTIVE_STATUS_MAPPING = {
    [STUDY_PERSON_INCENTIVE_STATUS.PENDING]: INCENTIVE_DISPLAY_STATE.PENDING,
    [STUDY_PERSON_INCENTIVE_STATUS.PROCESSING]: INCENTIVE_DISPLAY_STATE.PROCESSING,
    [STUDY_PERSON_INCENTIVE_STATUS.FAILED]: INCENTIVE_DISPLAY_STATE.FAILED
};

export const getIncentiveDisplayState = studyPerson => {
    // Check participant status for payment failed (legacy/fallback)
    if (studyPerson.status === SP_STATUS_KEY_PAYMENT_FAILED) {
        return INCENTIVE_DISPLAY_STATE.FAILED;
    }

    // Direct mapping for pending/processing/failed statuses
    if (studyPerson.incentive_status in INCENTIVE_STATUS_MAPPING) {
        return INCENTIVE_STATUS_MAPPING[studyPerson.incentive_status];
    }

    // Only 'paid' status continues to legacy logic
    if (studyPerson.incentive_status !== STUDY_PERSON_INCENTIVE_STATUS.PAID) {
        return null;
    }

    // Backwards compatibility: legacy first-party payments (pre-2026) that have
    // incentive_status='paid' but no incentive_sent_at (still waiting for execution).
    // New records set incentive_sent_at when entering 'pending' status, so this
    // only applies to old data created before the backend change.
    const isFirstParty =
        studyPerson.incentive_type === INCENTIVE_TYPE.FIRST_PARTY ||
        studyPerson.incentive_type === INCENTIVE_TYPE.FIRST_PARTY_VIA_TREMENDOUS;

    if (isFirstParty && !studyPerson.incentive_sent_at) {
        return INCENTIVE_DISPLAY_STATE.PENDING;
    }

    return INCENTIVE_DISPLAY_STATE.PAID;
};
